import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { ApiError, del, errorMessage, get, patch, post, put } from '../api/client';
import type { Country, Dealer } from '../api/types';
import { IconExternal, IconPin, IconPlus, IconTrash } from '../components/icons';
import { DragHandle, SortableList } from '../components/sortable';
import { Field, Modal, PageHead, Spinner, Switch, useFeedback } from '../components/ui';
import { useT } from '../i18n';

interface DealersResponse {
  items: Dealer[];
  countries: Country[];
}

export function Dealers() {
  const { t } = useT();
  const qc = useQueryClient();
  const { toast, confirm } = useFeedback();
  const [editing, setEditing] = useState<Dealer | 'new' | null>(null);
  const { data, isPending } = useQuery({ queryKey: ['dealers'], queryFn: () => get<DealersResponse>('/admin/dealers') });

  // En fila y solo el último pinta la respuesta (ver Models.tsx).
  const reorder = useMutation({
    mutationKey: ['dealers', 'order'],
    scope: { id: 'dealers-order' },
    mutationFn: (items: Dealer[]) => put<DealersResponse>('/admin/dealers/order', { ids: items.map((d) => d.id) }),
    onMutate: (items) => qc.setQueryData<DealersResponse>(['dealers'], (old) => old && { ...old, items }),
    onSuccess: (res) => {
      if (qc.isMutating({ mutationKey: ['dealers', 'order'] }) <= 1) qc.setQueryData(['dealers'], res);
    },
    onError: (e) => {
      toast(errorMessage(e), 'error');
      void qc.invalidateQueries({ queryKey: ['dealers'] });
    },
  });

  return (
    <>
      <PageHead
        kicker={t('Red MTX')}
        title={t('Puntos de venta')}
        description={t('Los pines del mapa «Dónde nos encontramos» y el listado junto a él. El número de cada pin sale del orden de esta lista.')}
      >
        <button className="btn btn-primary" onClick={() => setEditing('new')}>
          <IconPlus /> {t('Nuevo punto')}
        </button>
      </PageHead>

      {isPending || !data ? (
        <Spinner />
      ) : data.items.length === 0 ? (
        <div className="panel empty-state">{t('Todavía no hay puntos de venta.')}</div>
      ) : (
        <div className="panel" style={{ overflow: 'hidden' }}>
          <SortableList items={data.items} getId={(d) => d.id} onReorder={(items) => reorder.mutate(items)}>
            {(d, i, handle) => (
              <div className="list-item">
                <DragHandle {...handle} />
                <span className="badge red" style={{ minWidth: 34, justifyContent: 'center' }}>
                  {String(i + 1).padStart(2, '0')}
                </span>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div className="row row-wrap" style={{ gap: 8 }}>
                    <strong>{d.pinLabel}</strong>
                    <span className="faint">· {d.name}</span>
                    {!d.visible && <span className="badge">{t('Oculto')}</span>}
                  </div>
                  <div className="faint" style={{ fontSize: 13 }}>
                    {d.address}
                    {d.neighborhood && `. ${d.neighborhood}`}, {d.city} · {d.phone || t('sin teléfono')}
                  </div>
                </div>
                <div className="actions">
                <a className="btn btn-sm btn-ghost" href={`https://www.google.com/maps?q=${d.lat},${d.lng}`} target="_blank" rel="noreferrer" title={t('Ver en Google Maps')}>
                  <IconPin /> {d.lat.toFixed(4)}, {d.lng.toFixed(4)}
                </a>
                <button className="btn btn-sm" onClick={() => setEditing(d)}>
                  {t('Editar')}
                </button>
                <button
                  className="btn btn-sm btn-ghost btn-icon"
                  title={t('Eliminar')}
                  onClick={async () => {
                    if (!(await confirm(t('¿Eliminar el punto «{name}»?', { name: d.pinLabel }), { confirmLabel: t('Eliminar'), danger: true }))) return;
                    try {
                      await del(`/admin/dealers/${d.id}`);
                      toast(t('Punto eliminado'));
                      void qc.invalidateQueries({ queryKey: ['dealers'] });
                    } catch (e) {
                      toast(errorMessage(e), 'error');
                    }
                  }}
                >
                  <IconTrash />
                </button>
                </div>
              </div>
            )}
          </SortableList>
        </div>
      )}

      {editing && data && (
        <DealerDialog
          dealer={editing === 'new' ? null : editing}
          countries={data.countries}
          onClose={() => setEditing(null)}
          onSaved={() => {
            setEditing(null);
            void qc.invalidateQueries({ queryKey: ['dealers'] });
          }}
        />
      )}
    </>
  );
}

const EMPTY = { countryCode: 'NI', name: 'MTX Nicaragua', address: '', neighborhood: '', city: '', department: '', postalCode: '', phone: '', email: '', pinLabel: '', visible: true };

/** "12.1291, -86.2320" (como lo copia Google Maps) → [12.1291, -86.232] */
function parseCoords(text: string): [number, number] | null {
  const m = text.trim().match(/^(-?\d+(?:[.,]\d+)?)\s*[,;\s]\s*(-?\d+(?:[.,]\d+)?)$/);
  if (!m) return null;
  const [lat, lng] = [Number(m[1]!.replace(',', '.')), Number(m[2]!.replace(',', '.'))];
  return Number.isFinite(lat) && Number.isFinite(lng) ? [lat, lng] : null;
}

function DealerDialog({ dealer, countries, onClose, onSaved }: { dealer: Dealer | null; countries: Country[]; onClose: () => void; onSaved: () => void }) {
  const { t } = useT();
  const { toast } = useFeedback();
  const [f, setF] = useState(() => (dealer ? { ...dealer } : { ...EMPTY }));
  const [coords, setCoords] = useState(dealer ? `${dealer.lat}, ${dealer.lng}` : '');
  const [error, setError] = useState<ApiError | null>(null);
  const [busy, setBusy] = useState(false);
  const parsed = parseCoords(coords);
  const set = (k: keyof typeof EMPTY, v: string | boolean) => setF((p) => ({ ...p, [k]: v }));

  const save = async () => {
    if (!parsed) {
      setError(new ApiError(400, t('Pega las coordenadas como «12.1291, -86.2320»')));
      return;
    }
    setBusy(true);
    setError(null);
    const body = {
      countryCode: f.countryCode,
      name: f.name,
      address: f.address,
      neighborhood: f.neighborhood,
      city: f.city,
      department: f.department,
      postalCode: f.postalCode,
      phone: f.phone,
      email: f.email,
      pinLabel: f.pinLabel,
      visible: f.visible,
      lat: parsed[0],
      lng: parsed[1],
    };
    try {
      if (dealer) await patch(`/admin/dealers/${dealer.id}`, body);
      else await post('/admin/dealers', body);
      toast(dealer ? t('Punto actualizado') : t('Punto agregado'));
      onSaved();
    } catch (e) {
      setError(e instanceof ApiError ? e : new ApiError(0, errorMessage(e)));
      setBusy(false);
    }
  };

  const input = (k: keyof typeof EMPTY, label: string, hint?: string, type = 'text') => (
    <Field label={label} hint={hint} error={error?.field(k)}>
      {(id) => <input id={id} type={type} className="input" value={String(f[k] ?? '')} onChange={(e) => set(k, e.target.value)} />}
    </Field>
  );

  return (
    <Modal
      title={dealer ? t('Editar {name}', { name: dealer.pinLabel }) : t('Nuevo punto de venta')}
      onClose={onClose}
      wide
      footer={
        <>
          <button className="btn" onClick={onClose}>
            {t('Cancelar')}
          </button>
          <button className="btn btn-primary" disabled={busy} onClick={save}>
            {busy ? t('Guardando…') : t('Guardar')}
          </button>
        </>
      }
    >
      {error && !error.errors.length && <div className="notice error">{error.message}</div>}
      <div className="grid grid-2">
        {input('pinLabel', t('Nombre en el pin'), t('Corto: el barrio o la ciudad (p. ej. Bello Horizonte)'))}
        {input('name', t('Nombre del punto'), t('Aparece en el listado junto al mapa'))}
      </div>
      {input('address', t('Dirección'))}
      <div className="grid grid-3">
        {input('neighborhood', t('Barrio'))}
        {input('city', t('Ciudad'))}
        {input('department', t('Departamento'))}
      </div>
      <div className="grid grid-3">
        {input('phone', t('Teléfono'))}
        {input('email', t('Correo'), undefined, 'email')}
        {input('postalCode', t('Código postal'))}
      </div>
      <div className="grid grid-2">
        <Field
          label={t('Coordenadas')}
          error={error?.field('lat') ?? error?.field('lng')}
          hint={
            parsed ? (
              <a href={`https://www.google.com/maps?q=${parsed[0]},${parsed[1]}`} target="_blank" rel="noreferrer">
                {t('Comprobar en Google Maps')} <IconExternal width={12} style={{ verticalAlign: '-1px' }} />
              </a>
            ) : (
              t('En Google Maps: clic derecho sobre el lugar → copia las coordenadas y pégalas aquí')
            )
          }
        >
          {(id) => <input id={id} className="input mono" placeholder="12.1291, -86.2320" value={coords} onChange={(e) => setCoords(e.target.value)} />}
        </Field>
        <Field label={t('País')}>
          {(id) => (
            <select id={id} className="select" value={f.countryCode} onChange={(e) => set('countryCode', e.target.value)}>
              {countries.map((c) => (
                <option key={c.code} value={c.code}>
                  {c.name}
                </option>
              ))}
            </select>
          )}
        </Field>
      </div>
      <Switch checked={f.visible} onChange={(v) => set('visible', v)} label={t('Visible en el sitio')} />
    </Modal>
  );
}
